import React, { useEffect, useState } from "react";
import { Table, Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";
import { fmtMoney } from "../../../utils/reportExport";

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "Draft", label: "Draft" },
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Requested", label: "Requested" },
    { value: "Partially Received", label: "Partially Received" },
    { value: "Received", label: "Received" },
    { value: "Rejected", label: "Rejected" },
];

const PurchaseRequestsReport = () => {
    const [suppliers, setSuppliers] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/masterdropdown/listof_activesuppliers.php')
            .then((res) => { if (mounted) setSuppliers(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    const columns = [
        { title: 'PR Ref', dataIndex: 'pr_ref', key: 'pr_ref', width: 130, sorter: (a, b) => (a.pr_ref || '').localeCompare(b.pr_ref || '') },
        { title: 'Date', dataIndex: 'pr_date', key: 'pr_date', width: 110, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.pr_date).valueOf() - dayjs(b.pr_date).valueOf() },
        { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
        { title: 'Items', dataIndex: 'total_items_count', key: 'total_items_count', align: 'center', width: 80 },
        { title: 'Sub Total', dataIndex: 'pr_sub_total', key: 'pr_sub_total', align: 'right', width: 120, render: (v) => fmtMoney(v) },
        { title: 'Tax', dataIndex: 'pr_total_tax', key: 'pr_total_tax', align: 'right', width: 110, render: (v) => fmtMoney(v) },
        { title: 'Discount', dataIndex: 'pr_total_discount', key: 'pr_total_discount', align: 'right', width: 110, render: (v) => fmtMoney(v) },
        { title: 'Final Amount', dataIndex: 'pr_final_amount', key: 'pr_final_amount', align: 'right', width: 130, sorter: (a, b) => (a.pr_final_amount || 0) - (b.pr_final_amount || 0), render: (v) => fmtMoney(v) },
        { title: 'Status', dataIndex: 'pr_status', key: 'pr_status', width: 130 },
        { title: 'Created By', dataIndex: 'creator_name', key: 'creator_name', width: 130 },
    ];

    return (
        <GenericReportPage
            title="Purchase Requests Report"
            subtitle="Purchase requests with supplier, item counts and amounts"
            breadcrumb={["Reports", "Inventory"]}
            fetchUrl="/reports/inventory/purchase_requests.php"
            rowKey="pr_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 4, placeholder: "PR ref, supplier, note" },
                { type: "select", key: "sup_id", label: "Supplier", col: 4, options: suppliers, showSearch: true },
                { type: "select", key: "status", label: "Status", col: 4, options: STATUS_OPTIONS },
                { type: "date", key: "dateRange", label: "PR Date", col: 6 },
            ]}
            columns={columns}
            tableSummary={(pageData) => (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={7}><b>Total ({pageData.length} rows)</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right"><b>{fmtMoney(pageData.reduce((s, r) => s + Number(r.pr_final_amount || 0), 0))}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={8} colSpan={2} />
                </Table.Summary.Row>
            )}
            pdf={{
                title: "PURCHASE REQUESTS REPORT",
                subtitle: "Purchase requests with supplier, item counts and amounts",
                columns: [
                    { header: "PR Ref" }, { header: "Date" }, { header: "Supplier" }, { header: "Items", align: "center" },
                    { header: "Sub Total", align: "right" }, { header: "Tax", align: "right" }, { header: "Discount", align: "right" },
                    { header: "Final Amount", align: "right" }, { header: "Status" }, { header: "Created By" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        r.pr_ref || "-",
                        dayjs(r.pr_date).format("YYYY-MM-DD"),
                        r.supplier_name || "-",
                        String(r.total_items_count ?? ""),
                        fmtMoney(r.pr_sub_total),
                        fmtMoney(r.pr_total_tax),
                        fmtMoney(r.pr_total_discount),
                        fmtMoney(r.pr_final_amount),
                        r.pr_status || "-",
                        r.creator_name || "-",
                    ]),
                totalRow: (rows) => [
                    "TOTAL",
                    "",
                    "",
                    "",
                    "",
                    "",
                    "",
                    fmtMoney(rows.reduce((s, r) => s + Number(r.pr_final_amount || 0), 0)),
                    "",
                    "",
                ],
                columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } },
            }}
            excel={{
                filename: "purchase-requests-report",
                sheetName: "Purchase Requests",
                columns: [
                    { title: "PR Ref", dataIndex: "pr_ref", width: 16 },
                    { title: "Date", dataIndex: "pr_date", width: 12 },
                    { title: "Supplier", dataIndex: "supplier_name", width: 24 },
                    { title: "Items", dataIndex: "total_items_count", width: 8 },
                    { title: "Sub Total", dataIndex: "pr_sub_total", width: 14 },
                    { title: "Tax", dataIndex: "pr_total_tax", width: 12 },
                    { title: "Discount", dataIndex: "pr_total_discount", width: 12 },
                    { title: "Final Amount", dataIndex: "pr_final_amount", width: 14 },
                    { title: "Status", dataIndex: "pr_status", width: 16 },
                    { title: "Created By", dataIndex: "creator_name", width: 18 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        pr_date: dayjs(r.pr_date).format("YYYY-MM-DD"),
                        pr_sub_total: fmtMoney(r.pr_sub_total),
                        pr_total_tax: fmtMoney(r.pr_total_tax),
                        pr_total_discount: fmtMoney(r.pr_total_discount),
                        pr_final_amount: fmtMoney(r.pr_final_amount),
                    })),
                extraRows: (rows) => [["TOTAL", "", "", "", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.pr_final_amount || 0), 0)), "", ""]],
            }}
        />
    );
};

export default PurchaseRequestsReport;
